import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, onSnapshot, orderBy, addDoc, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import '../Booking.css';

function Booking() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('services');
  const [services, setServices] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [cart, setCart] = useState([]);
  
  // Modal & Service states
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [activeService, setActiveService] = useState(null);

  // Address states
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    villa: '', street: '', landmark: '', city: '', phone: '', userName: '' 
  });

  // Schedule states
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState({ period: '', time: '' });
  const [dropoffDate, setDropoffDate] = useState('');
  const [dropoffTime, setDropoffTime] = useState({ period: '', time: '' });

  // Checkout States
  const [starchLevel, setStarchLevel] = useState('None');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [consentToggle, setConsentToggle] = useState(false);
  const [attachedPhoto, setAttachedPhoto] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Payment Success States
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [orderId, setOrderId] = useState('');

  const timeSlotsConfig = {
    Morning: ['08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM'],
    Afternoon: ['12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM'],
    Evening: ['04:00 PM', '05:00 PM', '06:00 PM', '07:00 PM', '08:00 PM']
  };

  const tabs = [
    { id: 'services', label: 'Select Services', icon: 'fas fa-tshirt' },
    { id: 'address', label: 'Select Address', icon: 'fas fa-map-marker-alt' },
    { id: 'schedule', label: 'Schedule', icon: 'fas fa-calendar-alt' },
    { id: 'checkout', label: 'Checkout', icon: 'fas fa-shopping-cart' }
  ];

  useEffect(() => {
    // 1. Services & Items Fetch
    const qServices = query(collection(db, "services"), orderBy("createdAt", "asc"));
    const unsubServices = onSnapshot(qServices, (snapshot) => {
      setServices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const qItems = query(collection(db, "items"), orderBy("name", "asc"));
    const unsubItems = onSnapshot(qItems, (snapshot) => {
      setAllItems(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // 2. Fetch User Saved Addresses
    const userPhone = localStorage.getItem('userPhone');
    let unsubAddresses = () => {};

    if (userPhone) {
      const qAddresses = query(
        collection(db, "addresses"), 
        where("userPhone", "==", userPhone),
        orderBy("createdAt", "desc")
      );
      unsubAddresses = onSnapshot(qAddresses, (snapshot) => {
        const addrData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAddresses(addrData);
        
        // ലോക്കൽ വേരിയബിൾ ഉപയോഗിച്ച് ആദ്യ അഡ്രസ്സ് സെലക്ട് ചെയ്യുന്നു 
        // ഇത് 'selectedAddress' എന്ന സ്റ്റേറ്റിനെ നേരിട്ട് ഡിപെൻഡൻസി ആക്കാതെ വാണിംഗ് ഒഴിവാക്കും
        if (addrData.length > 0) {
          setSelectedAddress(prev => prev || addrData[0].id);
        }
      });
    }

    return () => { 
      unsubServices(); 
      unsubItems(); 
      unsubAddresses();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateCart = (item, qtyChange) => {
    setCart(prevCart => {
      const existing = prevCart.find(i => i.id === item.id && i.serviceId === activeService.id);
      if (existing) {
        const newQty = existing.quantity + qtyChange;
        if (newQty <= 0) return prevCart.filter(i => !(i.id === item.id && i.serviceId === activeService.id));
        return prevCart.map(i => (i.id === item.id && i.serviceId === activeService.id) ? { ...i, quantity: newQty } : i);
      }
      if (qtyChange > 0) {
        return [...prevCart, { ...item, quantity: 1, serviceId: activeService.id, serviceName: activeService.name }];
      }
      return prevCart;
    });
  };

  const removeFromCart = (itemId, serviceId) => {
    setCart(prevCart => prevCart.filter(i => !(i.id === itemId && i.serviceId === serviceId)));
  };

  const calculateSubtotal = () => cart.reduce((sum, item) => sum + (item.offerPrice * item.quantity), 0);
  
  const subtotal = calculateSubtotal();
  const deliveryFee = subtotal > 100 ? 0 : 10; 
  const tax = subtotal * 0.05; 
  const totalPayable = subtotal + deliveryFee + tax;

  const handleAddAddress = async (e) => {
    e.preventDefault();
    const userPhone = localStorage.getItem('userPhone');
    if (!userPhone) return alert("Please login to save address");
    
    // നിർബന്ധമായും വേണ്ട ഫീൽഡുകൾ പരിശോധിക്കുന്നു
    if (!newAddress.userName || !newAddress.villa || !newAddress.street || !newAddress.city || !newAddress.phone) {
      return alert("Please fill mandatory fields");
    }

    setIsLoading(true);
    try {
      const addressToSave = {
        ...newAddress,
        userPhone: userPhone,
        createdAt: new Date()
      };
      
      // 1. ഫയർബേസിലേക്ക് ഡാറ്റ ചേർക്കുന്നു
      const docRef = await addDoc(collection(db, "addresses"), addressToSave);
      
      // 2. പുതിയ അഡ്രസ്സിനെ ലിസ്റ്റിലേക്ക് മാനുവലായി ഉടനെ ചേർക്കുന്നു (Instant UI Update)
      const addedAddress = { id: docRef.id, ...addressToSave };
      setAddresses(prev => [addedAddress, ...prev]); 
      
      // 3. പുതിയ അഡ്രസ്സ് തന്നെ സെലക്ട് ചെയ്യുന്നു
      setSelectedAddress(docRef.id);
      
      // 4. ഫോം ക്ലോസ് ചെയ്യുന്നു
      setShowAddressForm(false);
      setNewAddress({ villa: '', street: '', landmark: '', city: '', phone: '', userName: '' });
      
    } catch (error) {
      console.error("Error saving address:", error);
      alert("Failed to save address. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const getUpcomingDays = () => {
    const days = [];
    for (let i = 0; i < 6; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      let label = i === 0 ? "Today" : i === 1 ? "Tomorrow" : date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' });
      days.push({ full: date.toDateString(), label: label });
    }
    return days;
  };

  const handlePayment = async () => {
    const userPhone = localStorage.getItem('userPhone');
    if (!userPhone) return alert("Please login to complete booking");
    if (!consentToggle) return alert("Please agree to the consent before payment");

    setIsLoading(true);
    const addr = addresses.find(a => a.id === selectedAddress);

    const bookingData = {
      customerPhone: userPhone,
      customerName: addr?.userName,
      address: addr,
      cart: cart,
      scheduling: {
        pickup: { date: pickupDate, time: pickupTime.time },
        dropoff: { date: dropoffDate, time: dropoffTime.time }
      },
      preferences: {
        starchLevel: starchLevel,
        specialInstructions: specialInstructions
      },
      payment: {
        subtotal: subtotal.toFixed(2),
        deliveryFee: deliveryFee,
        tax: tax.toFixed(2),
        totalPayable: totalPayable.toFixed(2),
        status: 'Success'
      },
      orderStatus: 'Pending',
      createdAt: new Date()
    };

    try {
      const docRef = await addDoc(collection(db, "bookings"), bookingData);
      setOrderId(docRef.id);
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Booking Error:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const renderCartSidebar = () => (
    <div className="booking-cart-sidebar">
      <h4>Your Cart ({cart.length})</h4>
      <div className="cart-items-list">
        {cart.length === 0 ? <p>No items added</p> : cart.map((item, idx) => (
          <div key={idx} className="cart-item-row">
            <div className="cart-item-info">
              <span>{item.name}</span>
              <small>{item.serviceName}</small>
            </div>
            <div className="cart-item-right-group">
              <div className="cart-item-price">{item.quantity} × AED {item.offerPrice}</div>
              <button className="cart-remove-btn" onClick={() => removeFromCart(item.id, item.serviceId)}><i className="fas fa-times"></i></button>
            </div>
          </div>
        ))}
      </div>
      {cart.length > 0 && (
        <div className="cart-total-box">
          <span>Sub Total</span>
          <span>AED {subtotal.toFixed(2)}</span>
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    const withSidebarLayout = (content) => (
      <div className="services-selection-container">
        <div className="services-main-side">{content}</div>
        {renderCartSidebar()}
      </div>
    );

    switch (activeTab) {
      case 'services':
        return withSidebarLayout(
          <>
            <h3>Select Your Services</h3>
            <div className="services-grid-layout">
              {services.map((service) => (
                <div key={service.id} className="service-list-item" onClick={() => { setActiveService(service); setIsItemModalOpen(true); }}>
                  <div className="service-info-left">
                    <div className="service-icon-circle"><i className={service.icon}></i></div>
                    <div className="service-text-meta">
                      <h4>{service.name}</h4>
                      <p>Click to add items</p>
                    </div>
                  </div>
                  <div className="service-action-right"><i className="fas fa-plus-circle"></i></div>
                </div>
              ))}
            </div>
          </>
        );

        case 'address':
        return withSidebarLayout(
          <div className="address-section">
            <div className="section-header-flex">
              <h3>Delivery Address</h3>
              {/* അഡ്രസ്സുകൾ ഉണ്ടെങ്കിൽ മാത്രം കാണിക്കുന്ന ഒരു ചെറിയ ബട്ടൺ */}
              {addresses.length > 0 && !showAddressForm && (
                <button className="add-new-small" onClick={() => setShowAddressForm(true)}>
                  
                </button>
              )}
            </div>

            <div className="address-grid">
              {/* 1. സേവ് ചെയ്ത അഡ്രസ്സുകൾ ലിസ്റ്റ് ചെയ്യുന്നു */}
              {addresses.length > 0 ? (
                addresses.map((addr) => (
                  <div 
                    key={addr.id} 
                    className={`address-card ${selectedAddress === addr.id ? 'selected' : ''}`} 
                    onClick={() => setSelectedAddress(addr.id)}
                  >
                    {selectedAddress === addr.id && <i className="fas fa-check-circle select-icon"></i>}
                    <p><strong>{addr.userName}</strong></p>
                    <p>{addr.villa}, {addr.street}</p>
                    <p>{addr.city}</p>
                    <p><i className="fas fa-phone-alt"></i> {addr.phone}</p>
                  </div>
                ))
              ) : (
                /* അഡ്രസ്സുകൾ ഒന്നുമില്ലെങ്കിൽ കാണിക്കേണ്ട മെസേജ് */
                !showAddressForm && <p className="no-data-msg">No saved addresses found.</p>
              )}

              {/* 2. പുതിയ അഡ്രസ് ചേർക്കാനുള്ള കാർഡ് */}
              {!showAddressForm && (
                <div className="add-address-card" onClick={() => setShowAddressForm(true)}>
                  <i className="fas fa-plus-circle"></i>
                  <p>Add New Address</p>
                </div>
              )}
            </div>

            {/* 3. അഡ്രസ് ചേർക്കാനുള്ള മോഡൽ ഫോം */}
            {showAddressForm && (
              <div className="modal-overlay">
                <div className="item-selection-modal address-form-modal">
                  <div className="modal-head">
                    <h3>Add New Address</h3>
                    <button onClick={() => setShowAddressForm(false)}>&times;</button>
                  </div>
                  
                  {/* ഇവിടെയാണ് handleAddAddress ഉപയോഗിക്കുന്നത് */}
                  <form onSubmit={handleAddAddress} className="address-inputs-container">
                    <div className="input-group">
                      <input type="text" placeholder="Full Name *" required value={newAddress.userName} onChange={(e) => setNewAddress({...newAddress, userName: e.target.value})} />
                    </div>
                    <div className="input-group">
                      <input type="text" placeholder="Villa / Building Name *" required value={newAddress.villa} onChange={(e) => setNewAddress({...newAddress, villa: e.target.value})} />
                    </div>
                    <div className="input-group">
                      <input type="text" placeholder="Street / Area *" required value={newAddress.street} onChange={(e) => setNewAddress({...newAddress, street: e.target.value})} />
                    </div>
                    <div className="input-group">
                      <input type="text" placeholder="Landmark (Optional)" value={newAddress.landmark} onChange={(e) => setNewAddress({...newAddress, landmark: e.target.value})} />
                    </div>
                    <div className="input-group">
                      <input type="text" placeholder="City *" required value={newAddress.city} onChange={(e) => setNewAddress({...newAddress, city: e.target.value})} />
                    </div>
                    <div className="input-group">
                      <input type="tel" placeholder="Phone Number *" required value={newAddress.phone} onChange={(e) => setNewAddress({...newAddress, phone: e.target.value})} />
                    </div>

                    <div className="modal-actions">
                      <button type="button" className="btn-cancel" onClick={() => setShowAddressForm(false)}>Cancel</button>
                      <button type="submit" className="btn-done" disabled={isLoading}>
                        {isLoading ? 'Saving...' : 'Save Address'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        );

      

      case 'schedule':
        return withSidebarLayout(
          <div className="schedule-section">
            <h3 className="section-title">Schedule Pick-up & Delivery</h3>
            <div className="schedule-grid-container">
              <div className="schedule-box card-shadow">
                <div className="box-header">
                  <div className="icon-wrap pickup"><i className="fas fa-truck-loading"></i></div>
                  <h4>Pick-up Details</h4>
                </div>
                <label className="input-label">Select Date</label>
                <div className="date-box-grid">
                  {getUpcomingDays().map(day => (
                    <div key={day.full} className={`date-box ${pickupDate === day.full ? 'active' : ''}`} onClick={() => setPickupDate(day.full)}>
                      <span>{day.label.split(',')[0]}</span>
                      <small>{day.label.includes(',') ? day.label.split(',')[1] : ''}</small>
                    </div>
                  ))}
                </div>
                <div className="period-tabs">
                  {Object.keys(timeSlotsConfig).map(period => (
                    <button key={period} className={pickupTime.period === period ? 'active' : ''} onClick={() => setPickupTime({ period, time: '' })}>{period}</button>
                  ))}
                </div>
                {pickupTime.period && (
                  <div className="specific-time-grid animate-fade">
                    {timeSlotsConfig[pickupTime.period].map(t => (
                      <button key={t} className={pickupTime.time === t ? 'selected' : ''} onClick={() => setPickupTime({ ...pickupTime, time: t })}>{t}</button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="schedule-box card-shadow">
                <div className="box-header">
                  <div className="icon-wrap dropoff"><i className="fas fa-truck"></i></div>
                  <h4>Drop-off Details</h4>
                </div>
                <label className="input-label">Select Date</label>
                <div className="date-box-grid">
                  {getUpcomingDays().map(day => (
                    <div key={day.full} className={`date-box ${dropoffDate === day.full ? 'active' : ''}`} onClick={() => setDropoffDate(day.full)}>
                      <span>{day.label.split(',')[0]}</span>
                      <small>{day.label.includes(',') ? day.label.split(',')[1] : ''}</small>
                    </div>
                  ))}
                </div>
                <div className="period-tabs">
                  {Object.keys(timeSlotsConfig).map(period => (
                    <button key={period} className={dropoffTime.period === period ? 'active' : ''} onClick={() => setDropoffTime({ period, time: '' })}>{period}</button>
                  ))}
                </div>
                {dropoffTime.period && (
                  <div className="specific-time-grid animate-fade">
                    {timeSlotsConfig[dropoffTime.period].map(t => (
                      <button key={t} className={dropoffTime.time === t ? 'selected' : ''} onClick={() => setDropoffTime({ ...dropoffTime, time: t })}>{t}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'checkout':
        const addr = addresses.find(a => a.id === selectedAddress);
        return (
          <div className="checkout-page-container">
            <div className="checkout-main-grid">
              <div className="checkout-info-column">
                <div className="checkout-card card-shadow">
                  <div className="card-header-flex">
                    <h4><i className="fas fa-user"></i> Customer & Address</h4>
                  </div>
                  <div className="customer-info-box">
                    <p><strong>Name:</strong> {addr?.userName}</p>
                    <p><strong>Phone:</strong> {addr?.phone}</p>
                    <p><strong>Address:</strong> {addr?.villa}, {addr?.street}, {addr?.city}</p>
                  </div>
                  <div className="checkout-schedule-preview">
                    <div className="schedule-preview-item">
                      <div className="preview-icon pickup"><i className="fas fa-truck-loading"></i></div>
                      <div>
                        <small>Pickup Date & Time</small>
                        <p>{pickupDate} | {pickupTime.time}</p>
                      </div>
                    </div>
                    <div className="schedule-preview-item">
                      <div className="preview-icon dropoff"><i className="fas fa-truck"></i></div>
                      <div>
                        <small>Drop-off Date & Time</small>
                        <p>{dropoffDate} | {dropoffTime.time}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="checkout-card card-shadow mt-20">
                  <h4><i className="fas fa-magic"></i> Cloth Starch Level</h4>
                  <div className="starch-radio-group">
                    {['None', 'Light', 'Medium', 'Hard'].map(level => (
                      <label key={level} className="starch-option">
                        <input type="radio" name="starch" value={level} checked={starchLevel === level} onChange={(e) => setStarchLevel(e.target.value)} />
                        <span>{level}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="checkout-card card-shadow mt-20">
                  <h4><i className="fas fa-comment-dots"></i> Special Instructions</h4>
                  <textarea className="instruction-field" placeholder="Type your special instructions here..." value={specialInstructions} onChange={(e) => setSpecialInstructions(e.target.value)}></textarea>
                  <div className="photo-upload-section">
                    <label className="upload-label">
                      <i className="fas fa-camera"></i> Attach Photo (Optional)
                      <input type="file" accept="image/*" onChange={(e) => setAttachedPhoto(e.target.files[0])} style={{display: 'none'}} />
                    </label>
                    {attachedPhoto && <small className="file-name">{attachedPhoto.name}</small>}
                  </div>
                </div>

                <div className="checkout-card card-shadow mt-20 consent-box">
                  <div className="toggle-wrapper">
                    <input type="checkbox" id="consent" checked={consentToggle} onChange={() => setConsentToggle(!consentToggle)} />
                    <label htmlFor="consent" className="toggle-label"></label>
                  </div>
                  <p>Our laundry team will process all items with stains or damages without seeking your consent</p>
                </div>

                <div className="checkout-nav-bottom mt-20">
                  <button className="btn-checkout-back" onClick={() => setActiveTab('schedule')}>
                    <i className="fas fa-chevron-left"></i> Back to Schedule
                  </button>
                </div>
              </div>

              <div className="checkout-summary-column">
                <div className="checkout-card card-shadow">
                  <h4>Order Summary</h4>
                  <div className="checkout-items-list">
                    {cart.map((item, idx) => (
                      <div key={idx} className="checkout-item-row">
                        <span>{item.name} ({item.quantity})</span>
                        <span>AED {(item.offerPrice * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="price-breakdown">
                    <div className="price-row"><span>Sub Total</span><span>AED {subtotal.toFixed(2)}</span></div>
                    <div className="price-row"><span>Delivery Fee</span><span>{deliveryFee === 0 ? 'FREE' : `AED ${deliveryFee.toFixed(2)}`}</span></div>
                    <div className="price-row"><span>Tax (5%)</span><span>AED {tax.toFixed(2)}</span></div>
                    <div className="total-payable-row"><span>Total Payable</span><span>AED {totalPayable.toFixed(2)}</span></div>
                  </div>
                  <button className="btn-make-payment" disabled={isLoading} onClick={handlePayment}>
                    {isLoading ? "Processing..." : "Make Payment"} <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      default: return null;
    }
  };

  const handleNext = () => {
    if (activeTab === 'services') {
      if (cart.length === 0) return alert("Select items first!");
      setActiveTab('address');
    } else if (activeTab === 'address') {
      if (!selectedAddress) return alert("Please select an address!");
      setActiveTab('schedule');
    } else if (activeTab === 'schedule') {
      if (!pickupDate || !pickupTime.time || !dropoffDate || !dropoffTime.time) {
        return alert("Please select Date and Time for both Pickup & Drop-off");
      }
      setActiveTab('checkout');
    }
  };

  return (
    <div className="booking-page">
      <div className="booking-container">
        <div className="booking-sidebar">
          <h2>Book a Service</h2>
          <ul className="tab-list">
            {tabs.map((tab) => (
              <li key={tab.id} className={activeTab === tab.id ? 'active' : ''} onClick={() => {
                if (activeTab === 'services' && cart.length === 0) return;
                setActiveTab(tab.id);
              }}>
                <i className={tab.icon}></i> <span>{tab.label}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="booking-content">
          <div className="content-card">
            {renderContent()}
            {activeTab !== 'checkout' && (
              <div className="booking-navigation">
                <button className="btn-back" disabled={activeTab === 'services'} onClick={() => setActiveTab(tabs[tabs.findIndex(t => t.id === activeTab) - 1].id)}>Back</button>
                <button className="btn-next" onClick={handleNext}>Next Step</button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {isItemModalOpen && (
        <div className="modal-overlay" onClick={() => setIsItemModalOpen(false)}>
          <div className="item-selection-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <h3>{activeService?.name} - Select Items</h3>
              <button onClick={() => setIsItemModalOpen(false)}>&times;</button>
            </div>
            <div className="item-list-grid-scroll">
              {allItems.filter(item => item.serviceId === activeService?.id).map(item => {
                const cartItem = cart.find(i => i.id === item.id && i.serviceId === activeService.id);
                return (
                  <div key={item.id} className="item-row-card">
                    <div className="item-main-info">
                      <div className="modal-item-icon"><i className={item.icon}></i></div>
                      <div><strong>{item.name}</strong><p>AED {item.offerPrice}</p></div>
                    </div>
                    <div className="qty-controls">
                      <button onClick={() => updateCart(item, -1)}>-</button>
                      <span>{cartItem?.quantity || 0}</span>
                      <button onClick={() => updateCart(item, 1)}>+</button>
                    </div>
                  </div>
                );
              })}
            </div>
            <button className="btn-done" onClick={() => setIsItemModalOpen(false)}>Done</button>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="modal-overlay" style={{zIndex: 9999}}>
          <div className="item-selection-modal" style={{textAlign: 'center', padding: '40px', maxWidth: '400px'}}>
            <div style={{fontSize: '70px', color: '#28a745', marginBottom: '20px'}}>
              <i className="fas fa-check-circle"></i>
            </div>
            <h2 style={{margin: '10px 0'}}>Payment Successful!</h2>
            <p style={{color: '#666', marginBottom: '20px'}}>Your laundry order has been placed.</p>
            <div style={{background: '#f1f1f1', padding: '10px', borderRadius: '8px', fontSize: '12px', marginBottom: '20px'}}>
              ORDER ID: <strong>{orderId}</strong>
            </div>
            <button className="btn-done" style={{width: '100%'}} onClick={() => navigate('/')}>Go Home</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Booking;